import { useState } from "react";
import {
  AlertTriangle,
  Building2,
  Heart,
  ShieldCheck,
  Users,
} from "lucide-react";
import EquityInclusionSchoolsModal from "@/components/EquityInclusionSchoolsModal";
import HeroStatusChip from "@/components/HeroStatusChip";
import { toTitleCase } from "../utils/toTitleCase";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);
const pct = (v, digits = 1) => (typeof v === "number" ? `${v.toFixed(digits)}%` : "0%");

const buildEquityGaugeStyle = (value) => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(#8b5cf6 0 ${Math.min(value, 100)}%, rgba(139,92,246,.14) ${Math.min(value, 100)}% 100%)`,
});

const computeEquityIndex = (equityMetrics, overview) => {
  const genderParity = equityMetrics?.gender_parity_index ?? overview?.gender_parity_index ?? 0;
  const cwsnCoverage = equityMetrics?.cwsn_coverage_pct ?? 0;
  const menstrual = equityMetrics?.menstrual_hygiene_coverage ?? overview?.sanitary_pad_pct ?? 0;
  const totalSchools = overview?.total_schools ?? 1;
  const zeroGirls = equityMetrics?.schools_zero_girls_toilet ?? overview?.zero_girls_toilet_schools ?? 0;
  const zeroGirlsPct = (zeroGirls / totalSchools) * 100;

  return (
    Math.round(
      (Math.min(genderParity / 1.2, 1) * 100 * 0.25 +
        cwsnCoverage * 0.35 +
        menstrual * 0.2 +
        (100 - zeroGirlsPct) * 0.2) *
        10
    ) / 10
  );
};

const getEquityStatus = (score) => {
  if (score >= 90) return { label: "Strong", tone: "" };
  if (score >= 85) return { label: "Watch", tone: "warn" };
  return { label: "Priority", tone: "danger" };
};

const EQUITY_DRILL_CATEGORIES = {
  no_cwsn_access: { id: "no_cwsn_access", label: "CWSN Toilet Access" },
  no_menstrual_hygiene: { id: "no_menstrual_hygiene", label: "Menstrual Hygiene" },
};

const SnapshotCard = ({
  icon: Icon,
  iconTone,
  dotTone,
  title,
  value,
  sub,
  progress,
  progressColor,
  metaLeft,
  metaRight,
  clickable = false,
  onClick,
}) => (
  <article
    className={`ct-snapshot-card${clickable ? " clickable" : ""}`}
    role={clickable ? "button" : undefined}
    tabIndex={clickable ? 0 : undefined}
    onClick={clickable ? onClick : undefined}
    onKeyDown={
      clickable
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick?.();
            }
          }
        : undefined
    }
    title={clickable ? `View schools for ${title}` : undefined}
  >
    <div className="ct-snapshot-head">
      <div className={`ct-icon-box ${iconTone}`}>
        <Icon size={22} />
      </div>
      <span className={`ct-status-dot ${dotTone}`} />
    </div>
    <h3>{toTitleCase(title)}</h3>
    <div className="ct-snapshot-score">
      <strong>{value}</strong>
      <span>{sub}</span>
    </div>
    <div className="ct-progress-line">
      <i style={{ width: progress, background: progressColor }} />
    </div>
    <div className="ct-snapshot-meta">
      <span>{metaLeft}</span>
      <span>{metaRight}</span>
    </div>
  </article>
);

const BalanceRow = ({ label, value, maxValue, tone = "" }) => {
  const width = maxValue > 0 ? `${Math.min((value / maxValue) * 100, 100)}%` : "0%";
  return (
    <div className="ct-balance-row">
      <div className="ct-balance-label">{label}</div>
      <div className="ct-balance-track">
        <div className={`ct-balance-fill ${tone}`} style={{ width }} />
      </div>
      <div className="ct-balance-value">{fmt(value)}</div>
    </div>
  );
};

const ClassroomsToiletsEquityInclusionTab = ({ overview, equityMetrics, blockData, scopeLabel }) => {
  const [activeCategory, setActiveCategory] = useState(null);

  const openCategory = (key, value) => {
    const count = Number(value) || 0;
    if (count <= 0) return;
    setActiveCategory({ ...EQUITY_DRILL_CATEGORIES[key], value: fmt(value) });
  };

  if (!overview && !equityMetrics) {
    return (
      <div className="ct-empty-card">
        <Users className="mx-auto mb-3 opacity-40" size={40} />
        <p>No equity and inclusion data available for the selected scope.</p>
      </div>
    );
  }

  const genderParity = equityMetrics?.gender_parity_index ?? overview?.gender_parity_index ?? 0;
  const boysToilets = equityMetrics?.boys_toilets ?? overview?.boys_toilets_total ?? 0;
  const girlsToilets = equityMetrics?.girls_toilets ?? overview?.girls_toilets_total ?? 0;
  const toiletGap = equityMetrics?.toilet_gap ?? girlsToilets - boysToilets;
  const cwsnTotal = equityMetrics?.cwsn_total ?? overview?.cwsn_total ?? 0;
  const cwsnFunctionalPct = equityMetrics?.cwsn_functional_pct ?? overview?.cwsn_functional_pct ?? 0;
  const cwsnCoverage = equityMetrics?.cwsn_coverage_pct ?? 0;
  const cwsnGap = Math.round((100 - cwsnCoverage) * 10) / 10;
  const menstrualCoverage = equityMetrics?.menstrual_hygiene_coverage ?? overview?.sanitary_pad_pct ?? 0;
  const schoolsWithoutCwsn = equityMetrics?.schools_without_cwsn ?? 0;
  const schoolsWithoutMenstrual =
    equityMetrics?.schools_without_menstrual_hygiene ??
    Math.max(0, Math.round(((overview?.total_schools ?? 0) * (100 - menstrualCoverage)) / 100));
  const zeroGirlsSchools = equityMetrics?.schools_zero_girls_toilet ?? overview?.zero_girls_toilet_schools ?? 0;

  const equityIndex = computeEquityIndex(equityMetrics, overview);
  const genderParityStrong = genderParity >= 1;
  const cwsnGapActive = cwsnCoverage < 90;

  const maxToiletScale = Math.max(girlsToilets, boysToilets, cwsnTotal, 1);

  const rankedBlocks = [...(blockData ?? [])]
    .sort((a, b) => (b.equity_score ?? 0) - (a.equity_score ?? 0))
    .map((block, index) => ({
      ...block,
      rank: index + 1,
    }));

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="ct-equity-tab" data-testid="tab-equity-content" id="ct-equity-overview">
      <section className="ct-hero-card ct-hero-card-equity">
        <div className="ct-hero-layout">
          <div>
            <div className="ct-health-main">
              <div
                className="ct-gauge ct-gauge-equity"
                style={buildEquityGaugeStyle(equityIndex)}
                aria-label={`Equity readiness score ${pct(equityIndex)}`}
              >
                <div>
                  <strong>{pct(equityIndex)}</strong>
                  <span>Equity</span>
                </div>
              </div>
              <div className="ct-hero-copy">
                <h2>Equity & Inclusion Command Centre</h2>
                <p>
                  Girls&apos; toilet availability is {genderParityStrong ? "stronger than" : "comparable to"} boys&apos;
                  toilet capacity, but inclusive access still needs focused action for CWSN coverage and schools
                  without girls&apos; toilets.
                </p>
                <div className="ct-status-row">
                  <HeroStatusChip
                    chipClass="ct-status-chip"
                    variant="green"
                    onClick={!genderParityStrong ? () => scrollTo("ct-equity-snapshot") : undefined}
                    ariaLabel={!genderParityStrong ? "Gender parity watch — view equity snapshot" : undefined}
                  >
                    {genderParityStrong ? "Gender parity strong" : "Gender parity watch"}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="ct-status-chip"
                    variant="orange"
                    onClick={
                      cwsnGapActive && schoolsWithoutCwsn > 0
                        ? () => openCategory("no_cwsn_access", schoolsWithoutCwsn)
                        : undefined
                    }
                    ariaLabel={
                      cwsnGapActive && schoolsWithoutCwsn > 0
                        ? "CWSN gap active — view schools without CWSN access"
                        : undefined
                    }
                  >
                    {cwsnGapActive ? "CWSN gap active" : "CWSN coverage stable"}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="ct-status-chip"
                    variant={menstrualCoverage >= 95 ? "purple" : "orange"}
                    onClick={
                      schoolsWithoutMenstrual > 0
                        ? () => openCategory("no_menstrual_hygiene", schoolsWithoutMenstrual)
                        : undefined
                    }
                    ariaLabel={
                      schoolsWithoutMenstrual > 0
                        ? `Menstrual hygiene ${pct(menstrualCoverage, 0)} — view schools without coverage`
                        : undefined
                    }
                  >
                    Menstrual hygiene {pct(menstrualCoverage, 0)}
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="ct-summary-note">
              <div className="ct-icon-box purple">
                <ShieldCheck size={22} />
              </div>
              <div>
                <b>Primary officer action</b>
                <span>
                  Prioritize the {fmt(zeroGirlsSchools)} schools without girls&apos; toilets, then improve CWSN toilet
                  coverage from {pct(cwsnCoverage)} to above 90%.
                </span>
              </div>
            </div>
          </div>

          <div className="ct-metric-grid ct-metric-grid-2" aria-label="Equity and inclusion KPIs">
            <article className="ct-metric-card">
              <small>Girls Toilets</small>
              <strong>{fmt(girlsToilets)}</strong>
              <span className="ct-metric-sub">
                +{fmt(toiletGap)} over boys&apos; toilet count
              </span>
            </article>
            <article className="ct-metric-card">
              <small>Boys Toilets</small>
              <strong>{fmt(boysToilets)}</strong>
              <span className="ct-metric-sub">Gender parity ratio {genderParity.toFixed(2)}</span>
            </article>
            <article className="ct-metric-card warning">
              <small>CWSN Coverage</small>
              <strong>{pct(cwsnCoverage)}</strong>
              <span className="ct-metric-sub">Needs targeted inclusion sprint</span>
            </article>
            <article className="ct-metric-card">
              <small>Menstrual Hygiene</small>
              <strong>{pct(menstrualCoverage, 0)}</strong>
              <span className="ct-metric-sub">Provision enabled across scope</span>
            </article>
          </div>
        </div>
      </section>

      <div className="ct-section-title" id="ct-equity-snapshot">
        <div>
          <h2>Equity Readiness Snapshot</h2>
          <p>
            Important indicators are separated into readable cards so officers can quickly identify where inclusion is
            strong and where intervention is required.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-equity-actions")}>
          View action queue →
        </button>
      </div>

      <section className="ct-snapshot-grid">
        <SnapshotCard
          icon={Users}
          iconTone="purple"
          dotTone=""
          title="Gender Parity Index"
          value={genderParity.toFixed(2)}
          sub="Girls:Boys ratio"
          progress="100%"
          progressColor="var(--ct-purple)"
          metaLeft="Girls higher"
          metaRight="Healthy"
        />
        <SnapshotCard
          icon={Building2}
          iconTone="green"
          dotTone="amber"
          title="CWSN Toilet Access"
          value={fmt(cwsnTotal)}
          sub="Total toilets"
          progress={`${Math.min(cwsnCoverage, 100)}%`}
          progressColor="var(--ct-accent)"
          metaLeft={`Coverage ${pct(cwsnCoverage)}`}
          metaRight={`Gap ${pct(cwsnGap)}`}
          clickable={schoolsWithoutCwsn > 0}
          onClick={() => openCategory("no_cwsn_access", schoolsWithoutCwsn)}
        />
        <SnapshotCard
          icon={Heart}
          iconTone="pink"
          dotTone=""
          title="Menstrual Hygiene"
          value={pct(menstrualCoverage, 0)}
          sub="Provision"
          progress={`${Math.min(menstrualCoverage, 100)}%`}
          progressColor="#d946ef"
          metaLeft={menstrualCoverage >= 95 ? "Fully enabled" : "Expansion needed"}
          metaRight={menstrualCoverage >= 95 ? "Maintain" : "Priority"}
          clickable={schoolsWithoutMenstrual > 0}
          onClick={() => openCategory("no_menstrual_hygiene", schoolsWithoutMenstrual)}
        />
        <SnapshotCard
          icon={AlertTriangle}
          iconTone="orange"
          dotTone="red"
          title="Girls Toilet Exceptions"
          value={fmt(zeroGirlsSchools)}
          sub="Schools"
          progress={`${Math.min((zeroGirlsSchools / Math.max(overview?.total_schools ?? 1, 1)) * 100 * 8, 100)}%`}
          progressColor="var(--ct-danger)"
          metaLeft="No girls toilets"
          metaRight="High priority"
        />
      </section>

      <div className="ct-section-title" id="ct-equity-diagnostics">
        <div>
          <h2>Equity Diagnostics</h2>
          <p>
            Charts are redesigned from raw flat visuals into explanatory diagnostics: capacity balance, CWSN access
            funnel, and officer-ready interpretation.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-equity-ranking")}>
          Go to rankings →
        </button>
      </div>

      <section className="ct-analytics-grid">
        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Gender Infrastructure Balance</h3>
              <p>
                Girls&apos; toilet capacity is above boys&apos; toilet capacity, but {fmt(zeroGirlsSchools)} specific
                schools still need attention.
              </p>
            </div>
            <span className="ct-pill">Ratio {genderParity.toFixed(2)}</span>
          </div>
          <div className="ct-balance-wrap" aria-label="Gender toilet balance chart">
            <BalanceRow label="Girls Toilets" value={girlsToilets} maxValue={maxToiletScale} tone="purple" />
            <BalanceRow label="Boys Toilets" value={boysToilets} maxValue={maxToiletScale} />
            <BalanceRow label="CWSN Toilets" value={cwsnTotal} maxValue={maxToiletScale} tone="green" />
          </div>
          <div className="ct-comparison-note">
            <div className="ct-mini-stat">
              <b>+{fmt(toiletGap)}</b>
              <span>Girls toilet count over boys</span>
            </div>
            <div className="ct-mini-stat">
              <b>{fmt(zeroGirlsSchools)}</b>
              <span>Schools without girls toilets</span>
            </div>
            <div className="ct-mini-stat">
              <b>{pct(cwsnCoverage)}</b>
              <span>CWSN coverage rate</span>
            </div>
          </div>
        </article>

        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Inclusive Access Funnel</h3>
              <p>Shows where inclusive sanitation readiness narrows from availability to functional coverage.</p>
            </div>
            <span className="ct-pill">CWSN</span>
          </div>
          <div className="ct-funnel" aria-label="Inclusive access funnel">
            <div className="ct-funnel-step">
              <div className="ct-step-no">1</div>
              <div>
                <h4>CWSN toilets available</h4>
                <p>Total infrastructure capacity mapped in scope.</p>
              </div>
              <div className="ct-step-value">{fmt(cwsnTotal)}</div>
            </div>
            <div className="ct-funnel-step">
              <div className="ct-step-no teal">2</div>
              <div>
                <h4>CWSN toilets functional</h4>
                <p>Usable and operational facilities.</p>
              </div>
              <div className="ct-step-value">{pct(cwsnFunctionalPct)}</div>
            </div>
            <div className="ct-funnel-step">
              <div className="ct-step-no warn">3</div>
              <div>
                <h4>School coverage</h4>
                <p>Coverage gap remains the main inclusion concern.</p>
              </div>
              <div className="ct-step-value">{pct(cwsnCoverage)}</div>
            </div>
            <div className="ct-funnel-step">
              <div className="ct-step-no danger">4</div>
              <div>
                <h4>Priority gap</h4>
                <p>Schools requiring targeted inclusion support.</p>
              </div>
              <div className="ct-step-value">{pct(cwsnGap)}</div>
            </div>
          </div>
        </article>
      </section>

      <div className="ct-section-title" id="ct-equity-ranking">
        <div>
          <h2>Block-Wise Inclusion Ranking</h2>
          <p>
            The ranking table brings gender parity, CWSN coverage, and exception counts together so field teams can
            prioritize block-level reviews.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-equity-overview")}>
          Back to top ↑
        </button>
      </div>

      <section className="ct-two-column" id="ct-equity-actions">
        <article className="ct-panel ct-table-card">
          <div className="ct-table-head">
            <div>
              <h3>Equity Readiness by Block</h3>
              <p>Illustrative block-level view for quick district review.</p>
            </div>
            <span className="ct-pill">Top 8</span>
          </div>
          <table className="ct-infra-table" aria-label="Block-wise equity readiness rankings">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Block</th>
                <th>Equity Score</th>
                <th>Gender Ratio</th>
                <th>CWSN Coverage</th>
                <th>No Girls Toilet</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rankedBlocks.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", color: "var(--ct-muted)", fontWeight: 600 }}>
                    No block equity data available for the selected scope.
                  </td>
                </tr>
              ) : (
                rankedBlocks.slice(0, 8).map((block) => {
                  const status = getEquityStatus(block.equity_score ?? 0);
                  return (
                    <tr key={block.block_name}>
                      <td>{block.rank}</td>
                      <td>{block.block_name}</td>
                      <td className="ct-score">{block.equity_score?.toFixed(1) ?? "—"}</td>
                      <td>{(block.gender_ratio ?? genderParity).toFixed(2)}</td>
                      <td>{pct(block.cwsn_school_coverage_pct ?? cwsnCoverage)}</td>
                      <td>{fmt(block.zero_girls_toilet_schools ?? 0)}</td>
                      <td>
                        <span className={`ct-tag ${status.tone}`}>{status.label}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </article>

        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Focused interventions to convert equity indicators into field action.</p>
            </div>
            <span className="ct-pill">Priority</span>
          </div>
          <div className="ct-action-card">
            <div className="ct-equity-action-item">
              <div className="ct-icon-box orange">
                <AlertTriangle size={22} />
              </div>
              <div>
                <h4>Close girls-toilet exception list</h4>
                <p>
                  Resolve {fmt(zeroGirlsSchools)} schools without girls toilets through block-wise work orders and
                  weekly closure tracking.
                </p>
              </div>
              <span className="ct-priority high">High</span>
            </div>
            <div className="ct-equity-action-item">
              <div className="ct-icon-box green">
                <ShieldCheck size={22} />
              </div>
              <div>
                <h4>Improve CWSN coverage</h4>
                <p>
                  Prioritize blocks below 70% CWSN coverage and map schools without accessible toilet availability.
                </p>
              </div>
              <span className="ct-priority">Medium</span>
            </div>
            <div className="ct-equity-action-item">
              <div className="ct-icon-box pink">
                <Heart size={22} />
              </div>
              <div>
                <h4>Maintain menstrual hygiene readiness</h4>
                <p>
                  Keep {pct(menstrualCoverage, 0)} provision active through monthly inspection, inventory checks, and
                  school-level reporting.
                </p>
              </div>
              <span className="ct-priority maintain">Maintain</span>
            </div>
            <div className="ct-action-footer ct-action-footer-equity">
              <b>Next review recommendation</b>
              <span>
                Use a 30-day inclusion sprint: first close girls-toilet exceptions, then raise CWSN coverage by at least
                10 percentage points in priority blocks.
              </span>
            </div>
          </div>
        </article>
      </section>

      <EquityInclusionSchoolsModal
        open={Boolean(activeCategory)}
        category={activeCategory}
        onClose={() => setActiveCategory(null)}
        scopeLabel={scopeLabel}
      />
    </div>
  );
};

export default ClassroomsToiletsEquityInclusionTab;
